import { useEffect, useRef, useState, type ReactNode } from "react";

import { Animation } from "@allaria-tech/matter/Animation";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import { PageHeader } from "@allaria-tech/matter/PageHeader";

import Layout from "../Layout/Layout";

import CONFIG from "./utils/config";
import { loadScannerLibs } from "./utils/scripts";
import { isSecureContextOk, startCamera, stopCamera } from "./utils/camera";
import { computeMetrics, isCvReady } from "./utils/quality";
import { decideState, evaluateQuality } from "./utils/qualityDecision";
import { createFlow } from "./utils/flowMachine";
import { HINTS } from "./utils/messages";
import type { Rect, ScannerState } from "./interfaces/types";

import verifyDniIcon from "../../assets/images/icons/ui/dni-scan.svg";
import errorIcon from "../../assets/images/icons/alert/error-cross.svg";

import styles from "./ScannerDni.module.scss";

export type ScannerView = "initial" | "scanning" | "processing" | "error";

export interface ScannerActions {
  openCamera: () => void;
  uploadImages: () => void;
}

const FRONT_PROMPT: ReactNode = (
  <>
    Mostranos el <span className={styles.hintKeyword}>frente</span> del DNI, con
    la foto y el código de barras
  </>
);
const BACK_PROMPT: ReactNode = (
  <>
    ¡Frente listo! Ahora da vuelta el DNI y mostranos el{" "}
    <span className={styles.hintKeyword}>dorso</span>
  </>
);

const NO_DOCUMENT_TIMEOUT_MS = 5000;
const BACK_FLIP_GRACE_MS = 5000;
const SCAN_TIMEOUT_MS = 20000;

const CAMERA_ERROR_MESSAGES: Record<string, string> = {
  NotAllowedError: "Permiso de cámara denegado. Habilitalo en el navegador.",
  NotFoundError: "No se encontró una cámara.",
  NotSupportedError: "Este navegador no soporta la cámara.",
};

const STATE_COLORS: Record<ScannerState, string> = {
  ERROR: "#e53935",
  PENDING: "#a49925",
  SUCCESS: "#43a047",
};

interface LoaderState {
  show: boolean;
  text: string;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

const SMOOTH_ALPHA = 0.3;
const ema = (prev: number, next: number): number =>
  prev + SMOOTH_ALPHA * (next - prev);

interface SmoothedMetrics {
  quadAreaRatio: number;
  aspectRatio: number;
  sharpnessVariance: number;
  brightness: number;
  glareRatio: number;
}
const emptyMetrics = (): SmoothedMetrics => ({
  quadAreaRatio: 0,
  aspectRatio: 0,
  sharpnessVariance: 0,
  brightness: 0,
  glareRatio: 0,
});

interface CapturedSide {
  name: string;
  file: File;
}

interface ScannerDniProps {
  onScanDni: (front: CapturedSide, back: CapturedSide) => Promise<void> | void;
  onUploadImages?: () => void;
  onScanTimeout?: () => void;
  disabled?: boolean;
  isRemoteStep?: boolean;
  enableScan?: boolean;
  onViewChange?: (view: ScannerView, actions: ScannerActions) => void;
  /** Shows the camera's "Identidad" header with its back arrow outside the remote flow too. */
  showBackHeader?: boolean;
  testId?: string;
}

const ScannerDni = ({
  onScanDni,
  onUploadImages,
  onScanTimeout: onScanTimeoutProp,
  disabled,
  isRemoteStep,
  enableScan = true,
  onViewChange,
  showBackHeader,
  testId = "scanner-dni",
}: ScannerDniProps) => {
  const {
    verificationInfo,
    verificationInfoRemote,
    verifyIcon,
    infoContent,
    infoTitle,
    infoSubtitle,
    processingLoader,
    scanning,
    scanningInline,
    scanningLayout,
    scanningBody,
    stage,
    video: videoClass,
    videoBlur,
    guideWrap,
    guideWrapHidden,
    guideBox,
    hint,
    overlayMsg,
    frontSuccess,
  } = styles;

  const videoRef = useRef<HTMLVideoElement>(null);
  const blurVideoRef = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const guideBoxRef = useRef<HTMLDivElement>(null);

  const flowRef = useRef(createFlow());
  const flow = flowRef.current;
  const streamRef = useRef<MediaStream | null>(null);
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const guideRectRef = useRef<Rect | null>(null);
  const consecutiveOkRef = useRef(0);
  const lastStateRef = useRef<ScannerState>("ERROR");
  const rafIdRef = useRef<number | null>(null);
  const lastAnalysisRef = useRef(0);
  const noDocSinceRef = useRef<number | null>(null);
  const flipGraceUntilRef = useRef<number | null>(null);
  const scanDeadlineRef = useRef<number | null>(null);
  const smoothedRef = useRef<SmoothedMetrics>(emptyMetrics());
  const capturedRef = useRef<{
    front: CapturedSide | null;
    back: CapturedSide | null;
  }>({ front: null, back: null });

  const [view, setView] = useState<ScannerView>("initial");
  const [loader, setLoader] = useState<LoaderState>({ show: false, text: "" });
  const [scannerState, setScannerState] = useState<ScannerState>("PENDING");
  const [hintText, setHintText] = useState<ReactNode>(FRONT_PROMPT);
  const [showFrontSuccess, setShowFrontSuccess] = useState(false);

  const getAnalysisCanvas = (): HTMLCanvasElement => {
    if (!analysisCanvasRef.current) {
      analysisCanvasRef.current = document.createElement("canvas");
    }
    return analysisCanvasRef.current;
  };

  const computeRoi = (): Rect | null => {
    const stageEl = stageRef.current;
    const boxEl = guideBoxRef.current;
    const video = videoRef.current;
    if (!stageEl || !boxEl || !video) return null;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const stageBox = stageEl.getBoundingClientRect();
    const box = boxEl.getBoundingClientRect();
    const sw = stageBox.width;
    const sh = stageBox.height;
    if (!sw || !sh) return null;

    const scale = Math.max(sw / vw, sh / vh);
    const ox = (sw - vw * scale) / 2;
    const oy = (sh - vh * scale) / 2;

    const rx = clamp((box.left - stageBox.left - ox) / scale, 0, vw);
    const ry = clamp((box.top - stageBox.top - oy) / scale, 0, vh);
    const roi: Rect = {
      x: Math.round(rx),
      y: Math.round(ry),
      width: Math.round(clamp(box.width / scale, 1, vw - rx)),
      height: Math.round(clamp(box.height / scale, 1, vh - ry)),
    };
    guideRectRef.current = roi;
    return roi;
  };

  const syncClip = () => {
    const stageEl = stageRef.current;
    const boxEl = guideBoxRef.current;
    const sharp = videoRef.current;
    if (!stageEl || !boxEl || !sharp) return;
    const s = stageEl.getBoundingClientRect();
    const b = boxEl.getBoundingClientRect();
    const top = Math.max(0, b.top - s.top);
    const left = Math.max(0, b.left - s.left);
    const right = Math.max(0, s.right - b.right);
    const bottom = Math.max(0, s.bottom - b.bottom);
    const inset = `inset(${top}px ${right}px ${bottom}px ${left}px round 0.3125rem)`;
    sharp.style.clipPath = inset;
    sharp.style.setProperty("-webkit-clip-path", inset);
  };

  const syncAnalysisSize = (video: HTMLVideoElement): HTMLCanvasElement => {
    const analysis = getAnalysisCanvas();
    if (analysis.width !== video.videoWidth) analysis.width = video.videoWidth;
    if (analysis.height !== video.videoHeight)
      analysis.height = video.videoHeight;
    return analysis;
  };

  const cropSideToFile = (
    source: HTMLCanvasElement,
    rect: Rect,
    name: string,
  ): Promise<CapturedSide> =>
    new Promise((resolve, reject) => {
      const tmp = document.createElement("canvas");
      tmp.width = rect.width;
      tmp.height = rect.height;
      const tctx = tmp.getContext("2d");
      if (!tctx) return reject(new Error("No se pudo obtener el contexto 2D"));
      tctx.drawImage(
        source,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        rect.width,
        rect.height,
      );
      tmp.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("No se pudo generar la imagen"));
          resolve({
            name,
            file: new File([blob], name, { type: "image/jpeg" }),
          });
        },
        "image/jpeg",
        0.95, // higher fidelity so text stays crisp for OCR
      );
    });

  const currentPrompt = (): ReactNode =>
    flow.state.step === "BACK" ? BACK_PROMPT : FRONT_PROMPT;

  const resetFeedback = () => {
    consecutiveOkRef.current = 0;
    lastStateRef.current = "ERROR";
    noDocSinceRef.current = null;
    smoothedRef.current = emptyMetrics();
    setScannerState("PENDING");
    setHintText(currentPrompt());
  };

  const onCapture = async () => {
    if (lastStateRef.current !== "SUCCESS") return;
    flow.capture();

    const video = videoRef.current;
    const guideRect = guideRectRef.current;
    if (!video || !guideRect) return;
    const analysis = syncAnalysisSize(video);
    const actx = analysis.getContext("2d");
    if (!actx) return;
    actx.drawImage(video, 0, 0, analysis.width, analysis.height);

    const { step } = flow.state;
    const side = await cropSideToFile(
      analysis,
      guideRect,
      step === "BACK" ? "dni-dorso-escaneado.jpg" : "dni-frente-escaneado.jpg",
    ).catch(() => null);
    if (!side) return;
    if (step === "BACK") capturedRef.current.back = side;
    else capturedRef.current.front = side;

    await onAccept();
  };

  const onScanTimeout = () => {
    scanDeadlineRef.current = null;
    stopScanning();
    setView("error");
    onScanTimeoutProp?.();
  };

  const loop = (ts: number) => {
    rafIdRef.current = requestAnimationFrame(loop);
    if (flow.state.phase !== "CAPTURING") return;

    if (scanDeadlineRef.current != null && ts >= scanDeadlineRef.current) {
      onScanTimeout();
      return;
    }

    const video = videoRef.current;
    if (!isCvReady() || !video?.videoWidth) return;

    const interval = 1000 / CONFIG.analysisFps;
    if (ts - lastAnalysisRef.current < interval) return;
    lastAnalysisRef.current = ts;

    const roi = computeRoi();
    if (!roi) return;

    const analysis = syncAnalysisSize(video);
    const actx = analysis.getContext("2d");
    if (!actx) return;
    actx.drawImage(video, 0, 0, analysis.width, analysis.height);

    let raw;
    try {
      raw = computeMetrics(analysis, roi);
    } catch {
      return;
    }

    const sm = smoothedRef.current;
    sm.quadAreaRatio = ema(sm.quadAreaRatio, raw.quadAreaRatio ?? 0);
    if (raw.aspectRatio != null)
      sm.aspectRatio = ema(sm.aspectRatio, raw.aspectRatio);
    sm.sharpnessVariance = ema(sm.sharpnessVariance, raw.sharpnessVariance);
    sm.brightness = ema(sm.brightness, raw.brightness);
    sm.glareRatio = ema(sm.glareRatio, raw.glareRatio);

    const quality = evaluateQuality(sm, CONFIG, flow.state.step);

    // While flipping to the back, freeze capture progress so the front (which
    // may still be in frame) can't be grabbed as the back.
    const inFlipGrace =
      flow.state.step === "BACK" &&
      flipGraceUntilRef.current != null &&
      ts < flipGraceUntilRef.current;

    consecutiveOkRef.current = inFlipGrace
      ? 0
      : clamp(
          consecutiveOkRef.current + (quality.allPass ? 1 : -1),
          0,
          CONFIG.stableFramesToGreen,
        );
    const state = decideState(quality, consecutiveOkRef.current, CONFIG);
    lastStateRef.current = state;

    if (quality.documentDetected) {
      noDocSinceRef.current = null;
    } else if (noDocSinceRef.current == null) {
      noDocSinceRef.current = ts;
    }
    const noDocTooLong =
      !quality.documentDetected &&
      noDocSinceRef.current != null &&
      ts - noDocSinceRef.current >= NO_DOCUMENT_TIMEOUT_MS;

    const renderState: ScannerState =
      state === "ERROR" && !noDocTooLong ? "PENDING" : state;

    if (inFlipGrace) {
      setScannerState("PENDING");
      setHintText(currentPrompt());
    } else {
      setScannerState(renderState);
      if (state === "SUCCESS") {
        setHintText(HINTS.PROCESSING);
      } else if (!quality.documentDetected) {
        setHintText(noDocTooLong ? HINTS.NO_DOCUMENT : currentPrompt());
      } else if (quality.allPass) {
        setHintText(HINTS.PROCESSING);
      } else {
        setHintText(quality.reasons[0] || currentPrompt());
      }
    }

    if (state === "SUCCESS") onCapture();
  };

  const startScanning = async () => {
    if (!isSecureContextOk()) {
      setLoader({
        show: true,
        text: "La web no es segura para iniciar la cámara.",
      });
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    try {
      setLoader({ show: true, text: "Abriendo cámara…" });
      streamRef.current = await startCamera(video);
      const blur = blurVideoRef.current;
      if (blur) {
        blur.srcObject = streamRef.current;
        blur.play().catch(() => {});
      }
      setLoader({ show: false, text: "" });
      syncClip();
      scanDeadlineRef.current = performance.now() + SCAN_TIMEOUT_MS;
      rafIdRef.current = requestAnimationFrame(loop);
    } catch (e) {
      const name = (e as { name?: string }).name;
      setLoader({
        show: true,
        text:
          (name && CAMERA_ERROR_MESSAGES[name]) || `Error de cámara: ${name}`,
      });
    }
  };

  const stopScanning = () => {
    if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = null;
    scanDeadlineRef.current = null;
    stopCamera(streamRef.current);
    streamRef.current = null;
  };

  const handleOpenCamera = () => {
    setView("scanning");
    requestAnimationFrame(() => void startScanning());
  };

  const handleUploadImages = () => {
    stopScanning();
    onUploadImages?.();
  };

  const handleBack = () => {
    stopScanning();
    flow.reset();
    guideRectRef.current = null;
    capturedRef.current = { front: null, back: null };
    flipGraceUntilRef.current = null;
    setLoader({ show: false, text: "" });
    setShowFrontSuccess(false);
    resetFeedback();
    setView("initial");
  };

  // Hands the stage back to the camera once the front is confirmed, giving the
  // user the grace window to turn the DNI around before capture resumes
  const startFlipGrace = () => {
    flipGraceUntilRef.current = performance.now() + BACK_FLIP_GRACE_MS;
    setShowFrontSuccess(false);
  };

  const onAccept = async () => {
    flow.accept();
    resetFeedback();
    if (flow.state.step === "BACK") {
      // No deadline yet: capture stays frozen for as long as the success
      // animation runs, and the flip grace only starts counting once it ends
      flipGraceUntilRef.current = Infinity;
      setShowFrontSuccess(true);
      scanDeadlineRef.current = flipGraceUntilRef.current + SCAN_TIMEOUT_MS;
    }
    if (flow.state.step === "DONE") {
      const { front, back } = capturedRef.current;
      if (front && back) {
        stopScanning();
        setView("processing");
        try {
          await onScanDni(front, back);
        } catch {
          setView("error");
        }
      }
    }
  };

  useEffect(() => {
    loadScannerLibs().catch((err) =>
      console.error("No se pudieron cargar las librerías del escáner", err),
    );
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      stopCamera(streamRef.current);
    };
  }, []);

  useEffect(() => {
    if (view !== "scanning") return;
    const stageEl = stageRef.current;
    if (!stageEl) return;
    const observer = new ResizeObserver(() => syncClip());
    observer.observe(stageEl);
    return () => observer.disconnect();
  }, [view]);

  // Let the parent mirror the footer when it owns the buttons.
  useEffect(() => {
    onViewChange?.(view, {
      openCamera: handleOpenCamera,
      uploadImages: handleUploadImages,
    });
  }, [view]);

  const infoClassName = `${verificationInfo} ${
    isRemoteStep ? verificationInfoRemote : ""
  }`;

  const content = (
    <>
      {view === "error" ? (
        <div className={infoClassName} data-testid={`${testId}-error`}>
          <img className={verifyIcon} src={errorIcon} alt="" />
          <div className={infoContent}>
            <h1 className={infoTitle} data-testid={`${testId}-error-title`}>
              No pudimos verificar tu DNI
            </h1>
            <p className={infoSubtitle}>
              Podés continuar con la verificación subiendo las imágenes desde tu
              dispositivo.
            </p>
          </div>
        </div>
      ) : view === "processing" ? (
        <div className={infoClassName} data-testid={`${testId}-processing`}>
          <div className={processingLoader}>
            <LoadingCircle small testId={`${testId}-processing-loader`} />
          </div>
          <div className={infoContent}>
            <h1
              className={infoTitle}
              data-testid={`${testId}-processing-title`}
            >
              Estamos procesando tu documento
            </h1>
            <p className={infoSubtitle}>
              Te vamos a redireccionar en breve para continuar con la apertura.
            </p>
          </div>
        </div>
      ) : view === "initial" ? (
        <div className={infoClassName} data-testid={`${testId}-initial`}>
          <img className={verifyIcon} src={verifyDniIcon} alt="" />
          <div className={infoContent}>
            <h1 className={infoTitle} data-testid={`${testId}-initial-title`}>
              Vamos a verificar tu DNI
            </h1>
            <p className={infoSubtitle}>
              Apoyalo sobre una superficie plana y asegurate de que haya buena
              luz para que se vean todos los datos del documento.
            </p>
          </div>
        </div>
      ) : (
        <div
          className={`${scanning} ${isRemoteStep || showBackHeader ? "" : scanningInline}`}
          data-dni-scanning
          data-testid={`${testId}-scanning`}
        >
          {(isRemoteStep || showBackHeader) && (
            <PageHeader
              title="Identidad"
              onBack={handleBack}
              backLabel="Volver"
            />
          )}

          <div className={stage} ref={stageRef} data-testid={`${testId}-stage`}>
            <video ref={blurVideoRef} className={videoBlur} playsInline muted />
            <video
              ref={videoRef}
              className={videoClass}
              playsInline
              muted
              data-testid={`${testId}-video`}
            />

            <div
              className={`${guideWrap} ${
                showFrontSuccess ? guideWrapHidden : ""
              }`}
            >
              <div
                ref={guideBoxRef}
                className={guideBox}
                style={{ borderColor: STATE_COLORS[scannerState] }}
                data-testid={`${testId}-guide-box`}
                data-scanner-state={scannerState}
              />
              <p className={hint} data-testid={`${testId}-hint`}>
                {hintText}
              </p>
            </div>

            {showFrontSuccess && (
              <Animation
                className={frontSuccess}
                name="success-mobile"
                onComplete={startFlipGrace}
                testId={`${testId}-front-success`}
              />
            )}

            <div
              className={overlayMsg}
              hidden={!loader.show}
              data-testid={`${testId}-overlay-message`}
            >
              {loader.text}
            </div>
          </div>
        </div>
      )}

      {isRemoteStep && view === "initial" && (
        <ButtonGroup
          container
          size="large"
          primary={
            enableScan
              ? {
                  text: "Abrir cámara",
                  onClick: handleOpenCamera,
                  isDisabled: disabled,
                  testId: `${testId}-open-camera`,
                }
              : {
                  text: "Cargar imágenes",
                  onClick: handleUploadImages,
                  testId: `${testId}-upload-images`,
                }
          }
          secondary={
            enableScan
              ? {
                  text: "Cargar imágenes",
                  onClick: handleUploadImages,
                  testId: `${testId}-upload-images`,
                }
              : undefined
          }
        />
      )}

      {isRemoteStep && view === "error" && (
        <ButtonGroup
          container
          size="large"
          primary={{
            text: "Aceptar",
            onClick: handleUploadImages,
            testId: `${testId}-accept`,
          }}
        />
      )}
    </>
  );

  if (!isRemoteStep) return content;

  return (
    <Layout
      hideStepper
      layoutClassName={view === "scanning" ? scanningLayout : undefined}
      className={view === "scanning" ? scanningBody : undefined}
      testId={`${testId}-layout`}
    >
      {content}
    </Layout>
  );
};

export default ScannerDni;
